import * as pc from 'playcanvas'
import type { IApiClient } from '../api/interface'
import { useArenaStore } from '../store'
import { setupScene, setHqSkybox } from './scene'
import { setupDistrict } from './world/districtScene'
import { setWorldRoot } from './world/worldRoot'
import { PLOTS, DISTRICT_SPAWN } from './world/district'
import {
  INTERIOR_ISLAND_Z, INTERIORS, entryThresholds, thresholdAt, interiorFor, streetExitFor,
  OFFICE_INTERIOR,
} from './world/interiors'
import { clearColliders, getColliders, setColliderPlacement } from './colliders'
import { initDraco } from './draco'
import {
  getAssetLoadDiagnostics,
  preloadAssets,
  resetAssetCache,
} from './assets'
import { createAssetZoneController } from './assetZones'
import { PlayerController } from './player'
import { StationSystem } from './world/stations'
import { StreamSystem } from './world/streams'
import { LabelSystem } from './world/labels'
import { ServerRoomSystem } from './world/serverRoom'
import { PresenceSystem, createPcAvatarRenderer } from './world/players'
import {
  BrowserWorldClient,
  type WorldPresenceTransport,
} from '../world/worldSocketClient'
import { GallerySystem } from './world/gallery'
import { AgentTalkSystem } from './world/agentTalk'
import { AgentRosterSystem } from './world/agentRoster'
import { HqProjectionSystem } from './world/hqProjection'
import { buildInteriors } from './world/interiorRegistry'
import {
  MAX_LIBRARY_RENDERED_RELATIONSHIPS,
  MAX_LIBRARY_RENDERED_SUBJECTS,
} from './world/libraryLayout'
import { IntroSweep } from './feel/intro'
import {
  createDrawCallRuns,
  maybeRunDrawCallHarness,
  meshInMainCameraFrustum,
  type CampusTransitionArtifact,
} from '../perf/drawCallHarness'
import {
  CAMPUS_DESTINATION_EVIDENCE,
  campusAcceptanceView,
  runCampusTraversalAcceptance,
  type CampusDestinationViewId,
  type CampusRenderEvidence,
  type CampusViewDiagnostics,
} from '../perf/campusAcceptance'
import {
  hqAcceptanceView,
  hqAcceptanceViewById,
  runHqProgramTraversalAcceptance,
  runHqTraversalAcceptance,
  type HqAcceptanceFloor,
  type HqAcceptanceViewId,
} from '../perf/hqAcceptanceHarness'
import { HQ_CIRCULATION, HQ_PROGRAM_ZONES } from './world/floorPrograms'
import { createSharedMonitorDisplay } from './world/monitorDisplay'
import { registerWorldTeleportHandler } from './world/navigation'
import { HqRoomActionSystem } from './world/hqRoomActionSystem'
import { hqRoomActionPose, type HqRoomActionId } from './world/hqRoomActions'
import {
  CAMPUS_AUXILIARY_ASSETS,
  CAMPUS_LANDMARKS,
  CAMPUS_ZONE_ASSET_FILES,
  campusLandmarkEntityName,
  isNearCampusAssetZone,
} from './world/campusLandmarks'
import {
  configurePcPostProcessing,
  createPcApplication,
  DeferredPcCanvas,
} from './PcCanvasLifecycle'

// Entry style: false = instant spawn on the office floor (user preference);
// true = the 4s cinematic sweep over the desks.
const INTRO_SWEEP_ENABLED = false
import { audioSystem } from './feel/audio'
import { runWorkplaceTraversalAcceptance } from '../perf/workplaceAcceptance'
import {
  layoutDatacentreProjection,
  type DatacentreProjectionState,
} from './world/campusRooms'

const MOCK_WORLD_TRANSPORT: WorldPresenceTransport = {
  connect(onRoster, onConnectionState) {
    onRoster([])
    onConnectionState('disconnected')
  },
  updatePose() {},
  changeRoom() {},
  disconnect() {},
}

const createPresenceTransport = (): WorldPresenceTransport =>
  import.meta.env.VITE_MOCK_DATA === 'true'
    ? MOCK_WORLD_TRANSPORT
    : new BrowserWorldClient()

const TICK_INTERVAL = 0.6 // seconds between keyboard-tick bursts
const EMPTY_RUN_POSITIONS = new Map<string, [number, number, number]>()

// Persists across component mounts within the same page session so the intro
// sweep never replays after the first play (even if PcCanvas unmounts/remounts).
let _sessionIntroPlayed = false

export function PcCanvas() {
  function initEngine(canvas: HTMLCanvasElement): () => void {
    const assetPreloadAbort = new AbortController()
    // Spin up the Draco decoder workers before any GLB load — all model
    // assets are KHR_draco_mesh_compression-compressed.
    initDraco()

    const app = createPcApplication(canvas)

    // Camera / player head entity
    const cameraEntity = new pc.Entity('camera')
    cameraEntity.addComponent('camera', {
      clearColor: new pc.Color(0.68, 0.85, 1.0),
      fov: 68, // vertical; ~100 horizontal at 16:9
      nearClip: 0.2, // keeps the depth ratio sane over a much longer far plane
      // The district is ~112 m across. farClip also CLAMPS the sun's shadow
      // distance, so a short one silently caps shadow range no matter what
      // the light asks for.
      farClip: 300,
    })
    app.root.addChild(cameraEntity)
    // Arrive at the gate, looking north up the stem at the anchor.
    cameraEntity.setPosition(DISTRICT_SPAWN.x, 1.65, DISTRICT_SPAWN.z)

    // ── Tone mapping + post-processing ────────────────────────────────────
    // ACES + an explicit exposure makes the four-floor dark→bright material
    // arc stable across devices while preserving headroom for the Core.
    const frame = configurePcPostProcessing(app, cameraEntity)

    // ── Animus Quarter ────────────────────────────────────────────────────
    // The exterior district, then the office built INSIDE the anchor building.
    // The office keeps its own local coordinates entirely; it is parented to
    // one entity that carries the quarter-turn and the offset onto its plot,
    // and colliders get the matching placement as they register.
    const assetZones = createAssetZoneController(app)
    const district = setupDistrict(app, assetZones)

    // The office lives on its own coordinate ISLAND, far from the district.
    // Putting it physically inside the anchor plot pushed its contents — the
    // gallery boards, the desk workers, the server robot — out through the
    // building's walls and onto the street, and left them blocking the door.
    // Off-island it also cannot alias the street's colliders.
    const interiorRoot = new pc.Entity('office-interior')
    interiorRoot.setPosition(0, 0, INTERIOR_ISLAND_Z)
    app.root.addChild(interiorRoot)

    // The world root stays pointed at the interior for the WHOLE session, not
    // just during setup: the station, gallery, server-room and character
    // systems spawn continuously as runs come and go, and they author in
    // office-local coordinates. Clearing it after setup sent every desk
    // worker, gallery board and the daemon robot into the district street at
    // their office coordinates — which is exactly what was showing up outside.
    // The district builds its own root explicitly, so it is unaffected.
    setWorldRoot(interiorRoot)
    setColliderPlacement({ rotY: 0, dx: 0, dz: INTERIOR_ISLAND_Z })
    let windowFillLight: pc.Entity
    let hqLighting: ReturnType<typeof setupScene>['hqLighting']
    let hqCore: ReturnType<typeof setupScene>['hqCore']
    let hqFacade: ReturnType<typeof setupScene>['hqFacade']
    let hqPrograms: ReturnType<typeof setupScene>['hqPrograms']
    try {
      // If setupScene throws, the placement must still be cleared: leaving it
      // at dz = -1000 offsets the NEXT mount's ground collider by a kilometre
      // and the player falls through the world the instant the district loads.
      ;({ windowFillLight, hqCore, hqFacade, hqPrograms, hqLighting } = setupScene(app, assetZones))
    } finally {
      setColliderPlacement(null)
    }

    // ── Enterable interiors ─────────────────────────────────────────────────
    // Board, Ops, Library and any future interior are built from a data
    // registry — each on its own coordinate island, each behind the same
    // collider-placement scope. Adding one is a row in interiorRegistry.ts plus
    // a system file; this call never changes.
    const interiors = buildInteriors(app, cameraEntity, assetZones)

    const assetsSettled = preloadAssets(
      app,
      (p) => useArenaStore.getState().setAssetProgress(p),
      assetPreloadAbort.signal,
    )
    const player = new PlayerController(cameraEntity)
    player.init()
    // The camera position set above is overwritten by the controller on the
    // first frame, so the spawn has to be told to the CONTROLLER — otherwise
    // the player starts at the old office position, mid-street, and never sees
    // the framed view of the anchor that the gate exists to compose.
    player.teleport(DISTRICT_SPAWN.x, DISTRICT_SPAWN.z, DISTRICT_SPAWN.yaw)
    player.setRecoveryPoint(DISTRICT_SPAWN.x, DISTRICT_SPAWN.z, DISTRICT_SPAWN.yaw)
    /** null = outdoors; otherwise the plot whose interior we are inside. */
    let insidePlot: string | null = null
    let requestedAssetZone: string | null = null
    const updateAssetZone = (x: number, z: number): void => {
      const desired = insidePlot !== null
        ? 'workplace'
        : insidePlot === null && isNearCampusAssetZone(x, z)
          ? 'campus'
          : null
      if (desired === requestedAssetZone) return
      requestedAssetZone = desired
      void assetZones.setActiveZone(desired).then((result) => {
        if (result.status === 'failed' && assetZones.getActiveZone() === result.zone) {
          console.warn(`[asset-zone] ${result.zone} loaded with failures:`, result.failures)
        }
      }).catch((error) => {
        console.warn(`[asset-zone] failed to switch to ${desired ?? 'none'}:`, error)
      })
    }
    // Browser acceptance views enter the off-island HQ directly instead of
    // crossing the P3 doorway trigger. Track that ownership explicitly so the
    // harness exercises the same floor lighting and sky state as production.
    let hqHarnessViewActive = false
    const withAcceptanceSupport = (pose: ReturnType<typeof hqAcceptanceView>) => ({
      ...pose,
      supportTops: [...new Set(getColliders().filter((box) =>
        pose.x >= box.minX && pose.x <= box.maxX &&
        pose.z >= box.minZ && pose.z <= box.maxZ &&
        Math.abs(box.maxY - pose.supportY) < 0.01,
      ).map((box) => box.maxY))],
    })
    const campusRenderEvidence = (entityName: string): CampusRenderEvidence => {
      const node = app.root.findByName(entityName)
      const renders = node instanceof pc.Entity
        ? node.findComponents('render') as pc.RenderComponent[]
        : []
      const meshes = renders.flatMap((render) => render.meshInstances ?? [])
      const camera = cameraEntity.camera
      const worldBounds = meshes.length > 0
        ? meshes.reduce((bounds, mesh) => {
            const center = mesh.aabb.center
            const half = mesh.aabb.halfExtents
            return {
              minX: Math.min(bounds.minX, center.x - half.x),
              maxX: Math.max(bounds.maxX, center.x + half.x),
              minY: Math.min(bounds.minY, center.y - half.y),
              maxY: Math.max(bounds.maxY, center.y + half.y),
              minZ: Math.min(bounds.minZ, center.z - half.z),
              maxZ: Math.max(bounds.maxZ, center.z + half.z),
            }
          }, {
            minX: Number.POSITIVE_INFINITY,
            maxX: Number.NEGATIVE_INFINITY,
            minY: Number.POSITIVE_INFINITY,
            maxY: Number.NEGATIVE_INFINITY,
            minZ: Number.POSITIVE_INFINITY,
            maxZ: Number.NEGATIVE_INFINITY,
          })
        : null
      return {
        entityName,
        renderComponents: renders.length,
        meshInstances: meshes.length,
        visibleMeshInstances: camera
          ? meshes.filter((mesh) => meshInMainCameraFrustum(mesh, camera)).length
          : 0,
        worldBounds,
      }
    }
    const campusViewDiagnostics = (id: CampusDestinationViewId): CampusViewDiagnostics => {
      const position = cameraEntity.getPosition()
      const forward = cameraEntity.forward
      const destination = CAMPUS_DESTINATION_EVIDENCE[id]
      return {
        schema: 'arena.campus-view-diagnostics.v1',
        viewId: id,
        camera: {
          x: position.x,
          y: position.y,
          z: position.z,
          yawDeg: Math.atan2(-forward.x, -forward.z) * 180 / Math.PI,
          pitchDeg: Math.asin(Math.max(-1, Math.min(1, forward.y))) * 180 / Math.PI,
        },
        assetZone: assetZones.getSnapshot(),
        expectedZoneAssets: CAMPUS_ZONE_ASSET_FILES,
        landmarks: [
          ...CAMPUS_LANDMARKS.map((landmark) => ({
            file: landmark.file,
            plotId: landmark.plotId,
            ...campusRenderEvidence(campusLandmarkEntityName(landmark)),
          })),
          ...CAMPUS_AUXILIARY_ASSETS.map((asset) => ({
            file: asset.file,
            plotId: asset.plotId,
            ...campusRenderEvidence(asset.entityName),
          })),
        ],
        destination: {
          ...destination,
          ...campusRenderEvidence(destination.entityName),
        },
      }
    }
    const drawCallHarnessAbort = new AbortController()
    let acceptanceFixturePrevious: Pick<
      ReturnType<typeof useArenaStore.getState>,
      'runs' | 'agents'
    > | undefined
    let acceptanceFixtureRuns: ReturnType<typeof createDrawCallRuns> | undefined
    let syntyFixturePrevious: Pick<
      ReturnType<typeof useArenaStore.getState>,
      'runs' | 'agents'
    > | undefined
    let libraryFixturePrevious: Pick<
      ReturnType<typeof useArenaStore.getState>,
      'knowledge' | 'remoteProjectionStatus'
    > | undefined
    let libraryFixtureKnowledge: ReturnType<
      typeof useArenaStore.getState
    >['knowledge'] | undefined
    let exchangeFixturePrevious: Pick<
      ReturnType<typeof useArenaStore.getState>,
      'runs' | 'health' | 'remoteProjectionStatus'
    > | undefined
    let exchangeFixtureRuns: ReturnType<
      typeof useArenaStore.getState
    >['runs'] | undefined
    let exchangeFixtureHealth: ReturnType<
      typeof useArenaStore.getState
    >['health'] | undefined
    let workshopFixturePrevious: Pick<
      ReturnType<typeof useArenaStore.getState>,
      'runs' | 'health' | 'remoteProjectionStatus'
    > | undefined
    let workshopFixtureRuns: ReturnType<
      typeof useArenaStore.getState
    >['runs'] | undefined
    let workshopFixtureHealth: ReturnType<
      typeof useArenaStore.getState
    >['health'] | undefined
    let cafeFixturePrevious: Pick<
      ReturnType<typeof useArenaStore.getState>,
      'agents' | 'chats' | 'runs' | 'remoteProjectionStatus' | 'apiClient'
    > | undefined
    let cafeFixtureApiClient: IApiClient | undefined
    let restoreCafeFixtureApi: (() => void) | undefined
    let cafeFixtureAgents: ReturnType<
      typeof useArenaStore.getState
    >['agents'] | undefined
    let cafeFixtureChats: ReturnType<
      typeof useArenaStore.getState
    >['chats'] | undefined
    let cafeFixtureRuns: ReturnType<
      typeof useArenaStore.getState
    >['runs'] | undefined
    let datacentreFixturePrevious: Pick<
      ReturnType<typeof useArenaStore.getState>,
      'health' | 'remoteProjectionStatus' | 'worldStateStatus'
    > | undefined
    let datacentreFixtureApplied: Pick<
      ReturnType<typeof useArenaStore.getState>,
      'health' | 'remoteProjectionStatus' | 'worldStateStatus'
    > | undefined
    let datacentreFixtureLastGood: DatacentreProjectionState | undefined
    const syntyProofFixtureAgents = [
      { id: 'agent-beta', name: 'Beta' },
      { id: 'agent-gamma', name: 'Gamma' },
      { id: 'agent-proof-2', name: 'Standing' },
    ]
    const syntyScaleFixtureAgents = Array.from({ length: 32 }, (_, index) => ({
      id: `agent-${index}`,
      name: `Agent ${String(index + 1).padStart(2, '0')}`,
    }))
    let readSyntyCharacterDiagnostics: () => unknown = () => []
    let readSyntyRosterLifecycleDiagnostics: () => unknown = () => ({
      roomActive: false,
      generation: 0,
      entryCount: 0,
    })
    let setSyntyCharacterState: (
      state: 'idle' | 'working' | 'waiting' | 'alarmed' | null
    ) => void = () => undefined
    let runCampusTransitionAcceptance:
      (() => Promise<CampusTransitionArtifact>) | null = null
    const setAcceptanceWorkloadFixture = (enabled: boolean): void => {
      if (enabled) {
        if (!acceptanceFixturePrevious) {
          const state = useArenaStore.getState()
          acceptanceFixturePrevious = { runs: state.runs, agents: state.agents }
          acceptanceFixtureRuns = createDrawCallRuns(new Date().toISOString())
        }
        useArenaStore.setState({ runs: acceptanceFixtureRuns, agents: [] })
        return
      }
      if (!acceptanceFixturePrevious) return
      if (useArenaStore.getState().runs === acceptanceFixtureRuns) {
        useArenaStore.setState(acceptanceFixturePrevious)
      }
      acceptanceFixturePrevious = undefined
      acceptanceFixtureRuns = undefined
    }
    const setSyntyFixture = (
      agents: readonly { id: string; name: string }[] | null,
    ): void => {
      if (agents) {
        if (!syntyFixturePrevious) {
          const state = useArenaStore.getState()
          syntyFixturePrevious = { runs: state.runs, agents: state.agents }
        }
        useArenaStore.setState({ runs: [], agents: [...agents] })
        return
      }
      if (!syntyFixturePrevious) return
      useArenaStore.setState(syntyFixturePrevious)
      syntyFixturePrevious = undefined
    }
    const setSyntyCharacterFixture = (enabled: boolean): void => {
      setSyntyFixture(enabled ? syntyProofFixtureAgents : null)
    }
    const setLibraryKnowledgeFixture = (
      fixture: 'empty' | 'maximum' | 'stale' | 'restore',
    ): void => {
      if (fixture === 'restore') {
        if (!libraryFixturePrevious) return
        if (useArenaStore.getState().knowledge === libraryFixtureKnowledge) {
          useArenaStore.setState(libraryFixturePrevious)
        }
        libraryFixturePrevious = undefined
        libraryFixtureKnowledge = undefined
        return
      }
      if (!libraryFixturePrevious) {
        const state = useArenaStore.getState()
        libraryFixturePrevious = {
          knowledge: state.knowledge,
          remoteProjectionStatus: state.remoteProjectionStatus,
        }
      }
      if (fixture === 'stale') {
        libraryFixtureKnowledge = useArenaStore.getState().knowledge
        useArenaStore.setState({ remoteProjectionStatus: 'stale' })
        return
      }
      libraryFixtureKnowledge = fixture === 'maximum'
        ? Array.from(
            { length: MAX_LIBRARY_RENDERED_SUBJECTS },
            (_, index) => ({
              id: `K-maximum-${index}`,
              title: `Measured subject ${index + 1}`,
              entityKind: index % 2 === 0 ? 'requirement' : 'task',
              relationships: index < MAX_LIBRARY_RENDERED_RELATIONSHIPS
                ? [{
                    to_id: `K-maximum-${(index + 1) % MAX_LIBRARY_RENDERED_SUBJECTS}`,
                    type: 'related',
                    confidence: 0.8,
                  }]
                : [],
            }),
          )
        : []
      useArenaStore.setState({
        knowledge: libraryFixtureKnowledge,
        remoteProjectionStatus: 'fresh',
      })
    }
    const setExchangeProjectionFixture = (
      fixture:
        | 'empty'
        | 'maximum'
        | 'loading'
        | 'stale'
        | 'disconnected'
        | 'restore',
    ): void => {
      if (fixture === 'restore') {
        if (!exchangeFixturePrevious) return
        const state = useArenaStore.getState()
        if (
          state.runs === exchangeFixtureRuns &&
          state.health === exchangeFixtureHealth
        ) {
          useArenaStore.setState(exchangeFixturePrevious)
        }
        exchangeFixturePrevious = undefined
        exchangeFixtureRuns = undefined
        exchangeFixtureHealth = undefined
        return
      }
      if (!exchangeFixturePrevious) {
        const state = useArenaStore.getState()
        exchangeFixturePrevious = {
          runs: state.runs,
          health: state.health,
          remoteProjectionStatus: state.remoteProjectionStatus,
        }
      }
      if (fixture === 'stale') {
        const state = useArenaStore.getState()
        exchangeFixtureRuns = state.runs.map((run) =>
          run.status === 'completed' || run.status === 'failed' ||
          run.status === 'cancelled' || run.stale
            ? run
            : { ...run, stale: true })
        exchangeFixtureHealth = null
        useArenaStore.setState({
          runs: exchangeFixtureRuns,
          health: exchangeFixtureHealth,
          remoteProjectionStatus: 'stale',
        })
        return
      }
      if (fixture === 'loading' || fixture === 'disconnected') {
        exchangeFixtureRuns = []
        exchangeFixtureHealth = null
        useArenaStore.setState({
          runs: exchangeFixtureRuns,
          health: exchangeFixtureHealth,
          remoteProjectionStatus: fixture,
        })
        return
      }
      const maximum = fixture === 'maximum'
      exchangeFixtureRuns = maximum
        ? createDrawCallRuns(new Date().toISOString()).slice(0, 10)
        : []
      exchangeFixtureHealth = {
        status: 'healthy',
        uptime: 1,
        activeRuns: exchangeFixtureRuns.length,
        queueDepth: maximum ? 7 : 0,
        version: 'browser-harness',
        checkedAt: new Date().toISOString(),
        activeAgents: maximum ? 11 : 0,
        poolSize: 16,
        plugins: [],
        costSummary: {
          since: '24h',
          windowStart: '2026-07-30T00:00:00.000Z',
          windowEnd: '2026-07-31T00:00:00.000Z',
          windowed: true,
          totalTokens: maximum ? 125_000 : 0,
          totalCostUsd: maximum ? 42.75 : 0,
          reportedUsd: maximum ? 30 : 0,
          estimatedUsd: maximum ? 12.75 : 0,
        },
      }
      useArenaStore.setState({
        runs: exchangeFixtureRuns,
        health: exchangeFixtureHealth,
        remoteProjectionStatus: 'fresh',
      })
    }
    const setWorkshopProjectionFixture = (
      fixture: 'empty' | 'maximum' | 'stale' | 'restore',
    ): void => {
      if (fixture === 'restore') {
        if (!workshopFixturePrevious) return
        const state = useArenaStore.getState()
        if (
          state.runs === workshopFixtureRuns &&
          state.health === workshopFixtureHealth
        ) {
          useArenaStore.setState(workshopFixturePrevious)
        }
        workshopFixturePrevious = undefined
        workshopFixtureRuns = undefined
        workshopFixtureHealth = undefined
        return
      }
      if (!workshopFixturePrevious) {
        const state = useArenaStore.getState()
        workshopFixturePrevious = {
          runs: state.runs,
          health: state.health,
          remoteProjectionStatus: state.remoteProjectionStatus,
        }
      }
      if (fixture === 'stale') {
        const state = useArenaStore.getState()
        workshopFixtureRuns = state.runs.map((run) =>
          run.status === 'completed' || run.status === 'failed' ||
          run.status === 'cancelled' || run.stale
            ? run
            : { ...run, stale: true })
        workshopFixtureHealth = null
        useArenaStore.setState({
          runs: workshopFixtureRuns,
          health: workshopFixtureHealth,
          remoteProjectionStatus: 'stale',
        })
        return
      }
      const maximum = fixture === 'maximum'
      workshopFixtureRuns = maximum
        ? createDrawCallRuns(new Date().toISOString()).slice(0, 6)
        : []
      const plugins = maximum
        ? Array.from({ length: 10 }, (_, index) => ({
            name: `measured-plugin-${String(index + 1).padStart(2, '0')}`,
            kind: index % 2 === 0 ? 'mcp' : 'tool',
            status: index === 2
              ? 'degraded' as const
              : index === 7
                ? 'down' as const
                : 'healthy' as const,
          }))
        : []
      const current = useArenaStore.getState().health
      workshopFixtureHealth = current
        ? {
            ...current,
            activeRuns: workshopFixtureRuns.length,
            plugins,
          }
        : {
            status: 'healthy',
            uptime: 1,
            activeRuns: workshopFixtureRuns.length,
            queueDepth: 0,
            version: 'browser-harness',
            checkedAt: new Date().toISOString(),
            activeAgents: workshopFixtureRuns.length,
            poolSize: 8,
            plugins,
          }
      useArenaStore.setState({
        runs: workshopFixtureRuns,
        health: workshopFixtureHealth,
        remoteProjectionStatus: 'fresh',
      })
    }
    const setCafeProjectionFixture = (
      fixture:
        | 'empty'
        | 'maximum'
        | 'loading'
        | 'stale'
        | 'disconnected'
        | 'restore',
    ): void => {
      if (fixture === 'restore') {
        if (!cafeFixturePrevious) return
        const state = useArenaStore.getState()
        if (
          state.agents === cafeFixtureAgents &&
          state.chats === cafeFixtureChats &&
          state.runs === cafeFixtureRuns &&
          state.apiClient === cafeFixtureApiClient
        ) {
          useArenaStore.setState(cafeFixturePrevious)
        }
        cafeFixturePrevious = undefined
        cafeFixtureAgents = undefined
        cafeFixtureChats = undefined
        cafeFixtureRuns = undefined
        restoreCafeFixtureApi?.()
        restoreCafeFixtureApi = undefined
        cafeFixtureApiClient = undefined
        return
      }
      if (!cafeFixturePrevious) {
        const state = useArenaStore.getState()
        cafeFixturePrevious = {
          agents: state.agents,
          chats: state.chats,
          runs: state.runs,
          remoteProjectionStatus: state.remoteProjectionStatus,
          apiClient: state.apiClient,
        }
      }
      if (fixture === 'loading' || fixture === 'disconnected' || fixture === 'stale') {
        cafeFixtureAgents = []
        cafeFixtureChats = []
        cafeFixtureRuns = []
        useArenaStore.setState({
          agents: cafeFixtureAgents,
          chats: cafeFixtureChats,
          runs: cafeFixtureRuns,
          remoteProjectionStatus: fixture,
        })
        return
      }
      const maximum = fixture === 'maximum'
      cafeFixtureAgents = maximum
        ? Array.from({ length: 9 }, (_, index) => ({
            id: `cafe-agent-${String(index + 1).padStart(2, '0')}`,
            name: `Café agent ${String(index + 1).padStart(2, '0')}`,
            canStartChat: true,
          }))
        : []
      const validChats = maximum
        ? cafeFixtureAgents.map((agent, index) => ({
            id: `cafe-chat-${String(index + 1).padStart(2, '0')}`,
            title: `Measured conversation ${index + 1}`,
            agentId: agent.id,
            revision: 1,
            canSend: index < 7,
            messageCount: 0,
            messages: [],
          }))
        : []
      cafeFixtureChats = maximum
        ? [
            ...validChats,
            {
              id: 'cafe-chat-unbound',
              title: 'Generic unbound conversation',
              revision: 1,
              canSend: true,
            },
            {
              id: 'cafe-chat-orphan',
              title: 'Orphan-bound conversation',
              agentId: 'not-in-roster',
              revision: 1,
              canSend: true,
            },
          ]
        : []
      cafeFixtureRuns = maximum
        ? createDrawCallRuns(new Date().toISOString())
            .slice(0, 4)
            .map((run, index) => ({
              ...run,
              agentId: cafeFixtureAgents?.[index]?.id,
              agentName: cafeFixtureAgents?.[index]?.name,
            }))
        : []
      const baseClient = cafeFixturePrevious.apiClient
      if (baseClient && !cafeFixtureApiClient) {
        const methods = [
          'getAgentDetail',
          'getChat',
          'sendChatMessage',
        ] as const
        const descriptors = new Map<PropertyKey, PropertyDescriptor | undefined>(
          methods.map((method) => [
            method,
            Object.getOwnPropertyDescriptor(baseClient, method),
          ]),
        )
        const originalAgentDetail = baseClient.getAgentDetail?.bind(baseClient)
        const originalGetChat = baseClient.getChat?.bind(baseClient)
        const originalSend = baseClient.sendChatMessage?.bind(baseClient)
        Object.defineProperties(baseClient, {
          getAgentDetail: {
            configurable: true,
            value: async (agentId: string) => {
              const agent = cafeFixtureAgents?.find(
                (candidate) => candidate.id === agentId,
              )
              if (agent) {
                return {
                  ...agent,
                  canInspect: false,
                  redactionReason: 'configuration_unavailable' as const,
                }
              }
              if (!originalAgentDetail) {
                throw new Error(`Harness agent ${agentId} is unavailable`)
              }
              return originalAgentDetail(agentId)
            },
          },
          getChat: {
            configurable: true,
            value: async (chatId: string) => {
              const chat = cafeFixtureChats?.find(
                (candidate) => candidate.id === chatId,
              )
              if (chat) {
                return {
                  ...chat,
                  messages: chat.messages?.map((message) => ({ ...message })) ?? [],
                }
              }
              if (!originalGetChat) {
                throw new Error(`Harness chat ${chatId} is unavailable`)
              }
              return originalGetChat(chatId)
            },
          },
          sendChatMessage: {
            configurable: true,
            value: async (chatId: string, message: string) => {
              const chats = cafeFixtureChats
              const chat = chats?.find((candidate) => candidate.id === chatId)
              if (!chats || !chat?.agentId || !chat.canSend) {
                if (originalSend) return originalSend(chatId, message)
                throw new Error(`Harness chat ${chatId} is not writable`)
              }
              const expectedRevision = chat.revision
              const userMessage = {
                id: `harness-user-${chatId}-${expectedRevision}`,
                role: 'user' as const,
                content: message,
                seq: (chat.messageCount ?? 0) + 1,
              }
              const assistantMessage = {
                id: `harness-assistant-${chatId}-${expectedRevision}`,
                role: 'assistant' as const,
                content: `Confirmed by ${chat.agentId}.`,
                seq: (chat.messageCount ?? 0) + 2,
              }
              const nextChat = {
                ...chat,
                revision: expectedRevision + 1,
                messageCount: (chat.messageCount ?? 0) + 2,
                messages: [
                  ...(chat.messages ?? []),
                  userMessage,
                  assistantMessage,
                ],
              }
              cafeFixtureChats = chats.map((candidate) =>
                candidate.id === chatId ? nextChat : candidate)
              const current = useArenaStore.getState()
              if (
                current.apiClient === baseClient &&
                current.chats === chats
              ) {
                useArenaStore.setState({ chats: cafeFixtureChats })
              }
              return {
                chat: nextChat,
                message: assistantMessage,
                turn: {
                  status: 'completed' as const,
                  agentId: chat.agentId,
                  expectedRevision,
                  operationId: `harness-operation-${chatId}-${expectedRevision}`,
                  userMessageId: userMessage.id,
                  userSeq: userMessage.seq,
                  messageId: assistantMessage.id,
                  seq: assistantMessage.seq,
                },
              }
            },
          },
        })
        restoreCafeFixtureApi = () => {
          for (const method of methods) {
            const descriptor = descriptors.get(method)
            if (descriptor) Object.defineProperty(baseClient, method, descriptor)
            else delete (baseClient as IApiClient & Record<string, unknown>)[method]
          }
        }
        cafeFixtureApiClient = baseClient
      }
      useArenaStore.setState({
        agents: cafeFixtureAgents,
        chats: cafeFixtureChats,
        runs: cafeFixtureRuns,
        remoteProjectionStatus: 'fresh',
      })
    }
    const setDatacentreProjectionFixture = (
      fixture:
        | 'empty'
        | 'unavailable'
        | 'maximum'
        | 'loading'
        | 'stale'
        | 'disconnected'
        | 'restore',
    ): void => {
      if (fixture === 'restore') {
        if (!datacentreFixturePrevious) return
        const state = useArenaStore.getState()
        if (
          datacentreFixtureApplied &&
          state.health === datacentreFixtureApplied.health &&
          state.remoteProjectionStatus === datacentreFixtureApplied.remoteProjectionStatus &&
          state.worldStateStatus === datacentreFixtureApplied.worldStateStatus
        ) {
          useArenaStore.setState(datacentreFixturePrevious)
        }
        datacentreFixturePrevious = undefined
        datacentreFixtureApplied = undefined
        datacentreFixtureLastGood = undefined
        return
      }
      if (!datacentreFixturePrevious) {
        const state = useArenaStore.getState()
        datacentreFixturePrevious = {
          health: state.health,
          remoteProjectionStatus: state.remoteProjectionStatus,
          worldStateStatus: state.worldStateStatus,
        }
      }
      if (fixture === 'loading' || fixture === 'stale' || fixture === 'disconnected') {
        datacentreFixtureApplied = {
          health: null,
          remoteProjectionStatus: fixture,
          worldStateStatus: fixture === 'disconnected' ? 'disconnected' : 'ready',
        }
        useArenaStore.setState(datacentreFixtureApplied)
        return
      }
      const plugins = fixture === 'maximum'
        ? [
            { name: 'agent-runtime', kind: 'application', status: 'healthy' as const },
            { name: 'chat-projection', kind: 'application', status: 'degraded' as const },
            { name: 'subject-projection', kind: 'application', status: 'down' as const },
            { name: 'world-relay', kind: 'application', status: 'unknown' as const },
            { name: 'github', kind: 'mcp', status: 'healthy' as const },
            { name: 'launchapp', kind: 'mcp', status: 'degraded' as const },
            { name: 'postgres', kind: 'storage', status: 'down' as const },
            { name: 'redis', kind: 'storage', status: 'unknown' as const },
            { name: 'railway', kind: 'transport', status: 'degraded' as const },
            { name: 'websocket', kind: 'transport', status: 'down' as const },
            { name: 'world-state', kind: 'world', status: 'unknown' as const },
          ]
        : fixture === 'empty'
          ? []
          : undefined
      const health: NonNullable<ReturnType<
        typeof useArenaStore.getState
      >['health']> = {
        status: 'healthy',
        uptime: 1,
        activeRuns: 0,
        queueDepth: 0,
        version: '1.2.3',
        checkedAt: new Date().toISOString(),
        activeAgents: 0,
        poolSize: 8,
        plugins,
      }
      const applied = {
        health,
        remoteProjectionStatus: 'fresh' as const,
        worldStateStatus: 'ready' as const,
      }
      datacentreFixtureApplied = applied
      useArenaStore.setState(applied)
      datacentreFixtureLastGood = layoutDatacentreProjection(
        'fresh',
        'ready',
        health,
      )
    }
    const setSyntyRosterScaleFixture = ({
      enabled,
      reverse = false,
    }: {
      enabled: boolean
      reverse?: boolean
    }): void => {
      setSyntyFixture(
        enabled
          ? reverse
            ? [...syntyScaleFixtureAgents].reverse()
            : syntyScaleFixtureAgents
          : null,
      )
    }
    void maybeRunDrawCallHarness(
      app,
      assetsSettled,
      window.location.search,
      drawCallHarnessAbort.signal,
      {
        capture: () => {
          const pose = player.getPos()
          return {
            x: pose.x,
            z: pose.z,
            yawDeg: player.getYaw() * 180 / Math.PI,
          }
        },
        showExterior: () => {
          insidePlot = null
          hqHarnessViewActive = false
          setHqSkybox(app, false)
          player.teleport(
            DISTRICT_SPAWN.x,
            DISTRICT_SPAWN.z,
            DISTRICT_SPAWN.yaw,
          )
        },
        showCampusView: (id) => {
          insidePlot = null
          hqHarnessViewActive = false
          setHqSkybox(app, false)
          const pose = campusAcceptanceView(id)
          player.teleport3d(
            pose.x,
            pose.y,
            pose.z,
            pose.yawDeg,
            pose.pitchDeg,
          )
          return pose
        },
        campusViewDiagnostics,
        // The 20-station fixture spans the production pods on L1 and L2. This
        // southeast atrium view keeps every resulting station batch in the
        // frustum while exercising the Core and the multi-floor shell.
        showWorkload: () => {
          insidePlot = 'P3'
          hqHarnessViewActive = true
          setHqSkybox(app, true)
          player.teleport(8, INTERIOR_ISLAND_Z + 7, -48)
        },
        showInterior: (plotId: string) => {
          const interior = interiorFor(plotId)
          if (!interior) throw new Error(`No built interior for ${plotId}`)
          insidePlot = plotId
          hqHarnessViewActive = plotId === 'P3'
          setHqSkybox(app, hqHarnessViewActive)
          player.teleport(interior.spawn.x, interior.spawn.z, interior.spawn.yaw)
        },
        setWorkloadFixture: setAcceptanceWorkloadFixture,
        setLibraryKnowledgeFixture,
        setExchangeProjectionFixture,
        exchangeProjectionFixtureDiagnostics: () => {
          const state = useArenaStore.getState()
          return {
            status: state.remoteProjectionStatus,
            healthPresent: state.health !== null,
            runningRuns: state.runs.filter(
              (run) => run.status === 'running' && !run.stale,
            ).length,
            staleRuns: state.runs.filter((run) => run.stale === true).length,
            queueDepth: state.health?.queueDepth ?? null,
            activeAgents: state.health?.activeAgents ?? null,
            poolSize: state.health?.poolSize ?? null,
            totalCostUsd: state.health?.costSummary?.totalCostUsd ?? null,
            totalTokens: state.health?.costSummary?.totalTokens ?? null,
          }
        },
        setWorkshopProjectionFixture,
        workshopProjectionFixtureDiagnostics: () => {
          const state = useArenaStore.getState()
          return {
            status: state.remoteProjectionStatus,
            healthPresent: state.health !== null,
            runningRuns: state.runs.filter(
              (run) => run.status === 'running' && !run.stale,
            ).length,
            staleRuns: state.runs.filter((run) => run.stale === true).length,
          }
        },
        setCafeProjectionFixture,
        cafeProjectionFixtureDiagnostics: () => {
          const state = useArenaStore.getState()
          const agentIds = new Set(state.agents.map((agent) => agent.id))
          const bound = state.chats.filter((chat) =>
            typeof chat.agentId === 'string' && agentIds.has(chat.agentId))
          return {
            status: state.remoteProjectionStatus,
            agents: state.agents.length,
            rawChats: state.chats.length,
            boundChats: bound.length,
            writableBoundChats: bound.filter((chat) => chat.canSend).length,
            unboundChats: state.chats.filter((chat) => !chat.agentId).length,
            orphanChats: state.chats.filter((chat) =>
              typeof chat.agentId === 'string' && !agentIds.has(chat.agentId)).length,
          }
        },
        setDatacentreProjectionFixture,
        datacentreProjectionFixtureDiagnostics: () => {
          const state = useArenaStore.getState()
          return layoutDatacentreProjection(
            state.remoteProjectionStatus,
            state.worldStateStatus,
            state.health,
            datacentreFixtureLastGood,
          )
        },
        blockingColliderCountAtPose: ({ x, z }) => getColliders().filter((box) =>
          box.maxY > 0 && box.minY < 1.8 &&
          x >= box.minX - 0.3 && x <= box.maxX + 0.3 &&
          z >= box.minZ - 0.3 && z <= box.maxZ + 0.3,
        ).length,
        showFloor: (floor: HqAcceptanceFloor) => {
          insidePlot = 'P3'
          hqHarnessViewActive = true
          setHqSkybox(app, true)
          const pose = hqAcceptanceView(floor, INTERIOR_ISLAND_Z)
          player.teleport3d(
            pose.x,
            pose.y,
            pose.z,
            pose.yawDeg,
            pose.pitchDeg,
          )
          return withAcceptanceSupport(pose)
        },
        showAcceptanceView: (id: HqAcceptanceViewId) => {
          insidePlot = 'P3'
          hqHarnessViewActive = true
          setHqSkybox(app, true)
          const pose = hqAcceptanceViewById(id, INTERIOR_ISLAND_Z)
          player.teleport3d(
            pose.x,
            pose.y,
            pose.z,
            pose.yawDeg,
            pose.pitchDeg,
          )
          return withAcceptanceSupport(pose)
        },
        showHqRoomAction: (id: HqRoomActionId) => {
          insidePlot = 'P3'
          hqHarnessViewActive = true
          setHqSkybox(app, true)
          const pose = hqRoomActionPose(id, INTERIOR_ISLAND_Z)
          player.teleport3d(
            pose.x,
            pose.y,
            pose.z,
            pose.yawDeg,
            pose.pitchDeg,
          )
          return pose
        },
        showSyntyCharacterProof: () => {
          insidePlot = 'P3'
          hqHarnessViewActive = true
          setHqSkybox(app, true)
          const pose = hqAcceptanceViewById('l1-synty-characters', INTERIOR_ISLAND_Z)
          player.teleport3d(pose.x, pose.y, pose.z, pose.yawDeg, pose.pitchDeg)
          return withAcceptanceSupport(pose)
        },
        showSyntyRosterFloor: (floor) => {
          insidePlot = 'P3'
          hqHarnessViewActive = true
          setHqSkybox(app, true)
          const pose = hqAcceptanceViewById(
            floor === 1 ? 'l1-synty-characters' : 'l2-synty-characters',
            INTERIOR_ISLAND_Z,
          )
          player.teleport3d(pose.x, pose.y, pose.z, pose.yawDeg, pose.pitchDeg)
          return withAcceptanceSupport(pose)
        },
        setSyntyCharacterFixture,
        setSyntyRosterScaleFixture,
        syntyCharacterDiagnostics: () => readSyntyCharacterDiagnostics(),
        syntyRosterLifecycleDiagnostics: () =>
          readSyntyRosterLifecycleDiagnostics(),
        assetLoadDiagnostics: (files) => getAssetLoadDiagnostics(files),
        assetZoneSnapshot: () => assetZones.getSnapshot(),
        setSyntyCharacterState: (state) => setSyntyCharacterState(state),
        runCampusTraversal: () => runCampusTraversalAcceptance(getColliders()),
        runCampusTransitions: () => {
          if (!runCampusTransitionAcceptance) {
            throw new Error('campus transition acceptance is not ready')
          }
          return runCampusTransitionAcceptance()
        },
        runWorkplaceTraversal: () =>
          runWorkplaceTraversalAcceptance(getColliders()),
        runTraversal: () => runHqTraversalAcceptance(getColliders()),
        runProgramTraversal: () => runHqProgramTraversalAcceptance(
          getColliders(),
          HQ_CIRCULATION,
          HQ_PROGRAM_ZONES,
        ),
        restore: (pose) => {
          const restoredInterior = INTERIORS.find((interior) =>
            pose.z >= interior.exitThreshold.minZ - 40 &&
            pose.z <= interior.exitThreshold.maxZ + 40)
          insidePlot = restoredInterior?.plotId ?? null
          hqHarnessViewActive = insidePlot === 'P3'
          setHqSkybox(app, hqHarnessViewActive)
          player.teleport(pose.x, pose.z, pose.yawDeg)
        },
      },
    ).catch((error: unknown) => {
      if (!drawCallHarnessAbort.signal.aborted) {
        console.error('[draw-call-harness]', error)
      }
    })

    // ── Feel systems ──────────────────────────────────────────────────────
    // IntroSweep — single-session; starts on first play, skippable
    const introSweep = new IntroSweep()

    // Keyboard tick accumulator
    let tickAccum = 0

    // ── World systems ─────────────────────────────────────────────────────
    const stationSystem  = new StationSystem()
    const streamSystem   = new StreamSystem()
    const labelSystem    = new LabelSystem()
    const serverRoom     = new ServerRoomSystem()
    const presenceSystem = new PresenceSystem()
    const gallerySystem  = new GallerySystem()
    const agentRoster    = new AgentRosterSystem()
    const agentTalk      = new AgentTalkSystem()
    const hqRoomActions  = new HqRoomActionSystem()
    const deskMonitorDisplay = createSharedMonitorDisplay(app)
    const hqProjection   = new HqProjectionSystem(app, hqCore, hqFacade, {
      projectHealth: (health) => serverRoom.projectHealth(health),
      projectRuns: (runs, agents, interactions, transcripts) => {
        stationSystem.projectRemoteState(runs, agents, interactions, transcripts)
        gallerySystem.projectRuns(runs)
      },
      projectAgentActivity: (interactions, transcripts) => {
        stationSystem.projectAgentActivity(interactions, transcripts)
      },
      projectProgramActivity: (activeRuns, recentCompletions) => {
        hqPrograms.projectActivity(activeRuns, recentCompletions)
      },
      projectProgramCooldown: (recentCompletionIds) => {
        hqPrograms.projectCooldown(recentCompletionIds)
      },
      projectProgramSummary: (summary) => {
        hqPrograms.projectSummary(summary)
      },
    })

    stationSystem.init(app, {
      externalProjection: true,
      monitorDisplay: deskMonitorDisplay,
      driveMonitorDisplay: false,
    })
    streamSystem.init(cameraEntity)
    labelSystem.init(app, cameraEntity)
    serverRoom.init(app, windowFillLight, { externalProjection: true })
    presenceSystem.init({
      renderer: createPcAvatarRenderer(app, cameraEntity),
      transport: createPresenceTransport(),
      getSelf: () => {
        const pos = player.getPos()
        return { x: pos.x, z: pos.z, yaw: player.getYaw() }
      },
    })
    gallerySystem.init(app, { externalProjection: true })
    agentRoster.init(app, { monitorDisplay: deskMonitorDisplay })
    readSyntyCharacterDiagnostics = () => agentRoster.getSyntyRosterDiagnostics()
    readSyntyRosterLifecycleDiagnostics = () =>
      agentRoster.getLifecycleDiagnostics()
    setSyntyCharacterState = (state) => agentRoster.setSyntyRosterState(state)
    agentTalk.init(app, cameraEntity, { acceptDirectoryRequests: true })
    hqRoomActions.init(app, interiorRoot, cameraEntity)
    hqProjection.init()

    // Track previous run statuses for audio transition detection
    const prevRunStatus = new Map<string, string>()

    const storeUnsub = useArenaStore.subscribe((state, prev) => {
      // First play → resume audio (AudioContext already created from the trusted
      // gesture in ClickToPlay.enter). The intro camera sweep is disabled by
      // user feedback ("we slide in") — spawn straight onto the office floor.
      // Re-enable by flipping INTRO_SWEEP_ENABLED if the feel is ever wanted.
      if (state.isPlaying && !prev.isPlaying) {
        audioSystem.init()
        audioSystem.startAmbience()
        if (INTRO_SWEEP_ENABLED && !_sessionIntroPlayed) {
          _sessionIntroPlayed = true
          player.introActive  = true
          introSweep.start()
        }
      }

      // Sync audio mute/volume
      if (state.audioMuted !== prev.audioMuted) audioSystem.setMuted(state.audioMuted)
      if (state.masterVolume !== prev.masterVolume) audioSystem.setVolume(state.masterVolume)

      // Run status transitions → audio feedback
      if (state.runs !== prev.runs) {
        for (const run of state.runs) {
          const ps = prevRunStatus.get(run.id)
          if (ps === 'running') {
            if (run.status === 'completed') audioSystem.playRunComplete()
            else if (run.status === 'failed') audioSystem.playRunFailed()
          }
        }
        for (const run of state.runs) prevRunStatus.set(run.id, run.status)
      }
    })

    // Sync initial audio state
    const { audioMuted, masterVolume } = useArenaStore.getState()
    audioSystem.setMuted(audioMuted)
    audioSystem.setVolume(masterVolume)

    // ── Building transitions ──────────────────────────────────────────────
    // Step into a doorway and you are inside; step out of the interior's own
    // door and you are back on the pavement. A short fade covers the jump.
    const thresholds = entryThresholds()
    const fade = document.createElement('div')
    fade.style.cssText =
      'position:absolute;inset:0;background:#0b0d10;opacity:0;pointer-events:none;' +
      'transition:opacity 140ms linear;z-index:20'
    canvas.parentElement?.appendChild(fade)

    let transitioning = false
    const fadeTimers: number[] = []

    function transitionTo(x: number, z: number, yaw: number, nowInside: string | null): void {
      if (transitioning) return
      transitioning = true
      audioSystem.playTransition()
      fade.style.opacity = '1'
      fadeTimers.push(window.setTimeout(() => {
        player.teleport(x, z, yaw)
        insidePlot = nowInside
        const roomPlot = nowInside ? PLOTS.find((plot) => plot.id === nowInside) : undefined
        presenceSystem.setRoom(nowInside === 'P3' ? 'office' : roomPlot?.domain ?? 'district')
        // HQ (the office, plot P3) looks out over the bay; everywhere else keeps
        // the street's own sky. Swapped under the fade so the sky never pops.
        setHqSkybox(app, nowInside === 'P3')
        fade.style.opacity = '0'
        fadeTimers.push(window.setTimeout(() => { transitioning = false }, 150))
      }, 150))
    }

    const unregisterWorldTeleport = registerWorldTeleportHandler((destination) => {
      transitionTo(
        destination.x,
        destination.z,
        destination.yaw,
        destination.plotId,
      )
    })

    function checkTransitions(pos: { x: number; y: number; z: number }): void {
      if (transitioning) return

      if (insidePlot === null) {
        const t = thresholdAt(thresholds, pos.x, pos.z)
        if (!t) return
        const interior = interiorFor(t.plotId)
        // Buildings without an interior yet simply stay shut.
        if (!interior) return
        transitionTo(interior.spawn.x, interior.spawn.z, interior.spawn.yaw, t.plotId)
        return
      }

      const interior = interiorFor(insidePlot)
      if (!interior) return
      const e = interior.exitThreshold
      if (pos.x >= e.minX && pos.x <= e.maxX && pos.z >= e.minZ && pos.z <= e.maxZ) {
        const plot = PLOTS.find((p) => p.id === insidePlot)
        const out = plot ? streetExitFor(plot) : OFFICE_INTERIOR.exit
        transitionTo(out.x, out.z, out.yaw, null)
      }
    }

    runCampusTransitionAcceptance = async (): Promise<CampusTransitionArtifact> => {
      const expectedPlots = ['P4', 'P5', 'P8'] as const
      const near = (
        actual: { x: number; z: number },
        expected: { x: number; z: number },
      ): boolean =>
        Math.abs(actual.x - expected.x) <= 0.05 &&
        Math.abs(actual.z - expected.z) <= 0.05
      const waitUntil = async (
        predicate: () => boolean,
        timeoutMs = 2_000,
      ): Promise<boolean> => {
        const startedAt = performance.now()
        while (performance.now() - startedAt < timeoutMs) {
          if (predicate()) return true
          await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()))
        }
        return predicate()
      }
      const observations: CampusTransitionArtifact['plots'] = []

      for (const plotId of expectedPlots) {
        const plot = PLOTS.find((candidate) => candidate.id === plotId)
        const interior = interiorFor(plotId)
        const entry = thresholds.find((candidate) => candidate.plotId === plotId)
        if (!plot || !interior || !entry) {
          observations.push({
            plotId,
            entryThreshold: entry ?? { minX: 0, maxX: 0, minZ: 0, maxZ: 0 },
            expectedInteriorSpawn: interior?.spawn ?? { x: 0, z: 0, yaw: 0 },
            observedInteriorSpawn: null,
            entryPassed: false,
            exitThreshold: interior?.exitThreshold ??
              { minX: 0, maxX: 0, minZ: 0, maxZ: 0 },
            expectedStreetExit: plot ? streetExitFor(plot) : { x: 0, z: 0, yaw: 0 },
            observedStreetExit: null,
            exitPassed: false,
            passed: false,
            failures: [`${plotId} production transition definition is incomplete`],
          })
          continue
        }

        const failures: string[] = []
        insidePlot = null
        hqHarnessViewActive = false
        setHqSkybox(app, false)
        const entryPoint = {
          x: (entry.minX + entry.maxX) / 2,
          z: (entry.minZ + entry.maxZ) / 2,
        }
        player.teleport(entryPoint.x, entryPoint.z, 0)
        const entryCompleted = await waitUntil(() =>
          !transitioning &&
          insidePlot === plotId &&
          near(player.getPos(), interior.spawn))
        const observedInteriorSpawn = entryCompleted ? player.getPos() : null
        if (!entryCompleted) {
          failures.push(
            `${plotId} did not complete its real exterior threshold → interior spawn transition`,
          )
        }

        let exitCompleted = false
        let observedStreetExit: { x: number; y: number; z: number } | null = null
        const streetExit = streetExitFor(plot)
        if (entryCompleted) {
          const exitPoint = {
            x: (interior.exitThreshold.minX + interior.exitThreshold.maxX) / 2,
            z: (interior.exitThreshold.minZ + interior.exitThreshold.maxZ) / 2,
          }
          player.teleport(exitPoint.x, exitPoint.z, interior.spawn.yaw)
          exitCompleted = await waitUntil(() =>
            !transitioning &&
            insidePlot === null &&
            near(player.getPos(), streetExit))
          observedStreetExit = exitCompleted ? player.getPos() : null
          if (!exitCompleted) {
            failures.push(
              `${plotId} did not complete its real interior exit → street transition`,
            )
          }
        }

        observations.push({
          plotId,
          entryThreshold: entry,
          expectedInteriorSpawn: interior.spawn,
          observedInteriorSpawn,
          entryPassed: entryCompleted,
          exitThreshold: interior.exitThreshold,
          expectedStreetExit: streetExit,
          observedStreetExit,
          exitPassed: exitCompleted,
          passed: entryCompleted && exitCompleted,
          failures,
        })
      }

      const exactCoveragePass =
        observations.length === expectedPlots.length &&
        expectedPlots.every((plotId) =>
          observations.some((observation) => observation.plotId === plotId))
      const entryPass = observations.every((observation) => observation.entryPassed)
      const exitPass = observations.every((observation) => observation.exitPassed)
      const passed = exactCoveragePass && entryPass && exitPass
      return {
        schema: 'arena.campus-transitions.v1',
        status: 'measured',
        capturedAt: new Date().toISOString(),
        plots: observations,
        gate: {
          expectedPlots,
          exactCoveragePass,
          entryPass,
          exitPass,
          passed,
        },
        passed,
      }
    }

    // ── Main update loop ──────────────────────────────────────────────────
    app.on('update', (dt: number) => {
      player.update(dt)
      const pp = player.getPos()
      updateAssetZone(pp.x, pp.z)
      hqLighting.update(pp.y, insidePlot === 'P3' || hqHarnessViewActive)
      // Feed the wayfinding UI (minimap + building callout). Dead-zoned in the store.
      useArenaStore.getState().setPlayerPose(pp.x, pp.z, player.getYaw())
      checkTransitions(pp)
      // Board room only polls + projects its cards while the player is inside it.
      interiors.frame(insidePlot)
      const hqRoomActive = insidePlot === 'P3' || hqHarnessViewActive
      // Make HQ residency authoritative before any system reads roster
      // positions this frame. Leaving HQ tears down character controllers and
      // update listeners immediately; re-entry reuses app-generation assets.
      agentRoster.setRoomActive(hqRoomActive)
      district.life.frame(dt)
      audioSystem.update(dt, pp.x, pp.z, pp.y, player.getYaw())
      const runningDeskCount = useArenaStore.getState().runs.reduce(
        (count, run) =>
          count + Number(run.status === 'running' && !run.stale),
        0,
      )
      deskMonitorDisplay.update(dt, runningDeskCount)

      // Intro sweep: override camera while sweeping, release when done
      const frame = introSweep.update(dt)
      if (frame) {
        cameraEntity.setPosition(frame.pos.x, frame.pos.y, frame.pos.z)
        cameraEntity.setEulerAngles(frame.pitchDeg, frame.yawDeg, 0)
      } else if (player.introActive && introSweep.isDone) {
        player.introActive = false
      }

      // Keyboard ticks from active desks
      if (audioSystem.isReady) {
        tickAccum += dt
        if (tickAccum >= TICK_INTERVAL) {
          tickAccum -= TICK_INTERVAL
          const runs      = useArenaStore.getState().runs
          const positions = new Map([
            ...stationSystem.getPositions(),
            ...agentRoster.getBoundRunPositions(),
          ])
          const listenerPos = player.getPos()
          const listenerYaw = player.getYaw()
          for (const run of runs) {
            if (run.status !== 'running') continue
            const deskPos = positions.get(run.id)
            if (deskPos && Math.random() < 0.4) {
              audioSystem.playKeyboardTick(deskPos, listenerPos, listenerYaw)
            }
          }
        }
      }

      const stationPositions = stationSystem.getPositions()
      const positions = new Map([
        ...stationPositions,
        ...agentRoster.getBoundRunPositions(),
      ])
      streamSystem.frame(dt, positions)
      labelSystem.frame(positions, gallerySystem.getBoards(), hqRoomActive)
      hqRoomActions.frame(hqRoomActive)
      agentRoster.frame(cameraEntity)
      agentTalk.setRoster(
        hqRoomActive ? agentRoster.getRoster() : interiors.getAgentTalkRoster(),
      )
      agentTalk.update(hqRoomActive ? stationPositions : EMPTY_RUN_POSITIONS)
      serverRoom.frame(dt)
      presenceSystem.frame(dt)
    })

    const onResize = () => app.resizeCanvas()
    window.addEventListener('resize', onResize)

    app.start()

    return () => {
      // Invalidate asset callbacks before PlayCanvas tears its registry down.
      // Registry teardown can synchronously finish pending requests; without
      // this fence they repopulate the cache and log failures for a dead app.
      assetPreloadAbort.abort()
      resetAssetCache()
      setAcceptanceWorkloadFixture(false)
      setLibraryKnowledgeFixture('restore')
      setExchangeProjectionFixture('restore')
      setWorkshopProjectionFixture('restore')
      setCafeProjectionFixture('restore')
      setDatacentreProjectionFixture('restore')
      unregisterWorldTeleport()
      drawCallHarnessAbort.abort()
      window.removeEventListener('resize', onResize)
      storeUnsub()
      introSweep.destroy()
      labelSystem.destroy()
      stationSystem.destroy()
      streamSystem.destroy()
      serverRoom.destroy()
      presenceSystem.destroy()
      gallerySystem.destroy()
      agentRoster.destroy()
      setSyntyCharacterFixture(false)
      deskMonitorDisplay.destroy()
      agentTalk.destroy()
      hqRoomActions.destroy()
      hqProjection.destroy()
      hqPrograms.destroy()
      hqCore.destroy()
      hqLighting.destroy()
      interiors.destroy()
      district.life.destroy()
      assetZones.destroy()
      player.destroy()
      for (const t of fadeTimers) window.clearTimeout(t)
      fade.remove()
      setWorldRoot(null)
      // The world (walls, columns, props) is built by setupScene, so its
      // colliders are torn down here rather than by the player.
      clearColliders()
      frame.destroy()
      app.destroy()
    }
  }

  return <DeferredPcCanvas initializeEngine={initEngine} />
}
